import {useParams} from 'react-router-dom';
import {useState, useMemo} from 'react';
import {useQuery, useMutation} from '@apollo/client';
import {
  Card,
  Table,
  Pagination,
  Typography,
  Tag,
  Spin,
  Button,
  Modal,
  Select,
  Alert,
} from 'antd';
import {ClockCircleOutlined, EditOutlined} from '@ant-design/icons';
import {
  ADMIN_TOTAL_EARNINGS_PAID_OUT,
  REFERRAL_PARTNER_MONTHLY_BREAKDOWN,
  REFERRAL_PARTNER_EARNINGS_SUMMARY,
  UPDATE_PARTNER_EARNING_STATUS,
} from '../resolvers';
import {currencySymbols} from 'utils/mock';
import {toast} from 'react-toastify';
import styles from './referralPartnerEarnings.module.scss';

const {Title, Text} = Typography;

export const ReferralPartnerEarnings = () => {
  const {id: partnerId} = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [newStatus, setNewStatus] = useState(undefined);

  const {data: earningsSummaryData, loading: earningsSummaryLoading} = useQuery(
    REFERRAL_PARTNER_EARNINGS_SUMMARY,
    {
      variables: {input: {partnerId}},
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
      onError: error => toast.error(error.message),
    },
  );

  const earningsSummary =
    earningsSummaryData?.referralPartnerGetEarningsSummary?.__typename ===
    'ReferralPartnerEarningsSummary'
      ? earningsSummaryData.referralPartnerGetEarningsSummary
      : null;

  // Summary query
  const {data: summaryData, loading: summaryLoading} = useQuery(
    ADMIN_TOTAL_EARNINGS_PAID_OUT,
    {
      variables: {
        partnerId,
      },
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
      fetchPolicy: 'no-cache',
    },
  );

  // Monthly breakdown query (paginated)
  const {
    data: breakdownData,
    loading: breakdownLoading,
    refetch,
  } = useQuery(REFERRAL_PARTNER_MONTHLY_BREAKDOWN, {
    variables: {
      input: {
        partnerId,
        page: currentPage,
        limit,
      },
    },
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const summary =
    summaryData?.adminGetTotalEarningsPaidOut?.__typename ===
    'ReferralPartnerEarningsPaidOut'
      ? summaryData.adminGetTotalEarningsPaidOut
      : null;

  const breakdownResponse =
    breakdownData?.referralPartnerGetMonthlyBreakdown?.__typename ===
    'ReferralPartnerMonthlyBreakdownResponse'
      ? breakdownData.referralPartnerGetMonthlyBreakdown
      : null;

  const breakdown = breakdownResponse?.breakdown || [];
  const totalDocs = breakdownResponse?.totalDocs || 0;
  const currentPageNum = breakdownResponse?.page || 1;

  const currency = summary?.currency || 'XOF';
  const currencySymbol = currencySymbols?.[currency]?.symbol || 'CFA';

  const handlePageChange = (page, pageSize) => {
    if (page === currentPage && pageSize === limit) return;
    setCurrentPage(page);
    setLimit(pageSize);
    refetch({
      input: {
        partnerId,
        page,
        limit: pageSize,
      },
    });
  };

  const [updateStatus, {loading: updateLoading}] = useMutation(
    UPDATE_PARTNER_EARNING_STATUS,
  );

  const handleUpdateStatus = () => {
    if (!selectedRecord || !newStatus) return;

    updateStatus({
      variables: {
        data: {
          trackerId: selectedRecord.id,
          paymentStatus: newStatus,
        },
      },
    })
      .then(({data}) => {
        const result = data?.adminUpdateReferralPartnerEarningStatus;
        if (result?.__typename === 'Error' || result?.success === false) {
          toast.error(result?.message || 'Failed to update status');
          return;
        }
        toast.success(result?.message || 'Status updated successfully');
        refetch();
        handleCloseModal();
      })
      .catch(error => {
        toast.error(error.message);
      });
  };

  const handleOpenModal = record => {
    setSelectedRecord(record);
    setNewStatus(undefined); // Reset status selection
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedRecord(null);
    setNewStatus(undefined);
  };

  const getStatusConfig = status => {
    const statusUpper = (status || '').toUpperCase();
    if (statusUpper === 'PENDING') {
      return {
        color: 'gold',
        label: 'Pending',
        icon: <ClockCircleOutlined />,
      };
    }
    if (statusUpper === 'PAID' || statusUpper === 'COMPLETED') {
      return {
        color: 'green',
        label: statusUpper === 'PAID' ? 'Paid' : 'Completed',
        icon: null,
      };
    }
    return {
      color: 'default',
      label: status?.replace(/_/g, ' ') || status || 'Unknown',
      icon: null,
    };
  };

  const columns = useMemo(
    () => [
      {
        title: 'PERIOD',
        key: 'period',
        dataIndex: 'period',
        fixed: 'left',
        width: 120,
      },
      {
        title: 'STATUS',
        key: 'status',
        dataIndex: 'status',
        render: status => {
          const config = getStatusConfig(status);
          return (
            <Tag color={config.color} className={styles.statusTag}>
              {config.icon && (
                <span className={styles.statusIcon}>{config.icon}</span>
              )}
              {config.label}
            </Tag>
          );
        },
        width: 130,
      },
      {
        title: 'TOTAL COMMISSION',
        key: 'totalCommission',
        dataIndex: 'totalCommission',
        render: (amount, record) => {
          const recordCurrency =
            currencySymbols?.[record?.currency]?.symbol || currencySymbol;
          return (
            <span className={styles.commission}>
              {recordCurrency}
              {Number(amount || 0).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          );
        },
        width: 160,
        align: 'right',
      },
      {
        title: 'VOLUME GENERATED',
        key: 'volumeGenerated',
        dataIndex: 'volumeGenerated',
        render: (amount, record) => {
          const recordCurrency =
            currencySymbols?.[record?.currency]?.symbol || currencySymbol;
          return (
            <span className={styles.volume}>
              {recordCurrency}
              {Number(amount || 0).toLocaleString('en-US')}
            </span>
          );
        },
        width: 160,
        align: 'right',
      },
      {
        title: 'PLATFORM FEES',
        key: 'platformFees',
        dataIndex: 'platformFees',
        render: (amount, record) => {
          const recordCurrency =
            currencySymbols?.[record?.currency]?.symbol || currencySymbol;
          return (
            <span className={styles.fees}>
              {recordCurrency}
              {Number(amount || 0).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          );
        },
        width: 140,
        align: 'right',
      },
      {
        title: 'NEW USERS INVESTED',
        key: 'totalNewInvestedUsers',
        dataIndex: 'totalNewInvestedUsers',
        render: count => {
          return (
            <span className={styles.newUsers}>
              {Number(count || 0).toLocaleString('en-US')}
            </span>
          );
        },
        width: 150,
        align: 'right',
      },
      {
        title: 'COMMISSION ON NEW USERS',
        key: 'commissionDueOnNewInvestedUsers',
        dataIndex: 'commissionDueOnNewInvestedUsers',
        render: (amount, record) => {
          const recordCurrency =
            currencySymbols?.[record?.currency]?.symbol || currencySymbol;
          return (
            <span className={styles.commission}>
              {recordCurrency}
              {Number(amount || 0).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          );
        },
        width: 200,
        align: 'right',
      },
      {
        title: 'ACADEMY SALES',
        key: 'academySalesCount',
        dataIndex: 'academySalesCount',
        render: count => (
          <span className={styles.newUsers}>
            {Number(count || 0).toLocaleString('en-US')}
          </span>
        ),
        width: 150,
        align: 'right',
      },
      {
        title: 'ACADEMY VOLUME',
        key: 'academyVolume',
        dataIndex: 'academyVolume',
        render: (amount, record) => {
          const recordCurrency =
            currencySymbols?.[record?.currency]?.symbol || currencySymbol;
          return (
            <span className={styles.volume}>
              {recordCurrency}
              {Number(amount || 0).toLocaleString('en-US')}
            </span>
          );
        },
        width: 160,
        align: 'right',
      },
      {
        title: 'COMMISSION ON ACADEMY SALES',
        key: 'commissionDueOnAcademySales',
        dataIndex: 'commissionDueOnAcademySales',
        render: (amount, record) => {
          const recordCurrency =
            currencySymbols?.[record?.currency]?.symbol || currencySymbol;
          return (
            <span className={styles.commission}>
              {recordCurrency}
              {Number(amount || 0).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          );
        },
        width: 230,
        align: 'right',
      },
      {
        title: 'ACTIONS',
        key: 'actions',
        fixed: 'right',
        render: (text, record) => {
          const isPending = (record.status || '').toUpperCase() === 'PENDING';
          return (
            <div
              style={{display: 'flex', gap: '8px', justifyContent: 'center'}}>
              {isPending && (
                <Button
                  type="primary"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleOpenModal(record)}>
                  Update Status
                </Button>
              )}
            </div>
          );
        },
        width: 180,
        align: 'center',
      },
    ],
    [currencySymbol],
  );

  if (summaryData?.adminGetTotalEarningsPaidOut?.__typename === 'Error') {
    return (
      <div className={styles.errorContainer}>
        <Text type="danger">
          {summaryData.adminGetTotalEarningsPaidOut.message ||
            'Failed to load earnings paid out'}
        </Text>
      </div>
    );
  }

  if (
    breakdownData?.referralPartnerGetMonthlyBreakdown?.__typename === 'Error'
  ) {
    return (
      <div className={styles.errorContainer}>
        <Text type="danger">
          {breakdownData.referralPartnerGetMonthlyBreakdown.message ||
            'Failed to load monthly breakdown'}
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.referralPartnerEarnings}>
      <div className={styles.header}>
        <Title level={2}>Earnings Breakdown</Title>
        <Text type="secondary">Track your earnings over time</Text>
      </div>

      <Alert
        type="info"
        showIcon
        style={{marginBottom: 16}}
        message="Total commission already includes the Academy component."
        description="Academy sales are attributed by a nightly job, so a transaction recorded today shows up tomorrow. Line items roll up on the 1st of each month, so the current month stays empty until then."
      />

      {/* Summary Cards */}
      <div className={styles.summaryCards}>
        <Card className={styles.summaryCard}>
          <div className={styles.cardHeader}>
            <div
              className={styles.cardIcon}
              style={{
                color: '#10b981',
                background: 'rgba(16, 185, 129, 0.1)',
              }}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
            </div>
            <Text className={styles.cardLabel}>Total Paid Out</Text>
          </div>
          {summaryLoading ? (
            <Spin size="small" />
          ) : (
            <div>
              <Title level={3} className={styles.cardValue}>
                {currencySymbol}
                {Number(summary?.totalPaidOut || 0).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Title>
              <Text type="secondary" style={{fontSize: '12px'}}>
                {summary?.paidOutCount || 0} Transactions
              </Text>
            </div>
          )}
        </Card>

        <Card className={styles.summaryCard}>
          <div className={styles.cardHeader}>
            <div
              className={styles.cardIcon}
              style={{
                color: '#f59e0b',
                background: 'rgba(245, 158, 11, 0.1)',
              }}>
              <ClockCircleOutlined style={{fontSize: '24px'}} />
            </div>
            <Text className={styles.cardLabel}>Pending Payouts</Text>
          </div>
          {summaryLoading ? (
            <Spin size="small" />
          ) : (
            <div>
              <Title level={3} className={styles.cardValue}>
                {currencySymbol}
                {Number(summary?.totalPending || 0).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Title>
              <Text type="secondary" style={{fontSize: '12px'}}>
                {summary?.pendingCount || 0} Pending
              </Text>
            </div>
          )}
        </Card>

        <Card className={styles.summaryCard}>
          <div className={styles.cardHeader}>
            <div
              className={styles.cardIcon}
              style={{
                color: '#8d6cfa',
                background: 'rgba(141, 108, 250, 0.1)',
              }}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                <line x1="12" y1="20" x2="12" y2="10"></line>
                <line x1="18" y1="20" x2="18" y2="4"></line>
                <line x1="6" y1="20" x2="6" y2="16"></line>
              </svg>
            </div>
            <Text className={styles.cardLabel}>Average Monthly</Text>
          </div>
          {earningsSummaryLoading ? (
            <Spin size="small" />
          ) : (
            <div>
              <Title level={3} className={styles.cardValue}>
                {currencySymbols?.[earningsSummary?.currency]?.symbol ||
                  currencySymbol}
                {Number(earningsSummary?.averageMonthly || 0).toLocaleString(
                  'en-US',
                  {minimumFractionDigits: 2, maximumFractionDigits: 2},
                )}
              </Title>
              <Text type="secondary" style={{fontSize: '12px'}}>
                Growth {Number(earningsSummary?.growthRate || 0).toFixed(2)}%
              </Text>
            </div>
          )}
        </Card>
      </div>

      {/* Monthly Breakdown Table */}
      <div className={styles.tableSection}>
        <Title level={4} className={styles.tableTitle}>
          Monthly Breakdown
        </Title>
        <div className={styles.tableContainer}>
          <Table
            columns={columns}
            dataSource={breakdown}
            loading={breakdownLoading}
            rowKey={(record, index) => record?.period || index}
            pagination={false}
            scroll={{x: 'max-content'}}
            className={styles.table}
          />

          {totalDocs > 0 && (
            <div className={styles.paginationContainer}>
              <Pagination
                current={currentPageNum}
                total={totalDocs}
                pageSize={limit}
                showSizeChanger
                pageSizeOptions={['10', '20', '50', '100']}
                onChange={handlePageChange}
                onShowSizeChange={handlePageChange}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`
                }
                className={styles.pagination}
              />
            </div>
          )}

          {!breakdownLoading && breakdown.length === 0 && (
            <div className={styles.emptyState}>
              <Text type="secondary">No monthly breakdown data available</Text>
            </div>
          )}
        </div>
      </div>

      <Modal
        title="Update Earning Status"
        visible={isModalVisible}
        onOk={handleUpdateStatus}
        onCancel={handleCloseModal}
        confirmLoading={updateLoading}
        okText="Update"
        okButtonProps={{disabled: !newStatus}}>
        <div style={{marginBottom: '16px'}}>
          <Text strong>Current Status:</Text>{' '}
          <Tag>{selectedRecord?.status}</Tag>
        </div>
        <div>
          <Text strong>New Status:</Text>
          <Select
            style={{width: '100%', marginTop: '8px'}}
            placeholder="Select new status"
            value={newStatus}
            onChange={setNewStatus}>
            <Select.Option value="COMPLETED">COMPLETED</Select.Option>
            <Select.Option value="FAILED">FAILED</Select.Option>
          </Select>
        </div>
      </Modal>
    </div>
  );
};
